import { cn } from '../lib/cn';
import {
  Dialog as DialogRoot,
  DialogContent as DialogContentPrimitive,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';

/**
 * Modal (erp-design-guideline.md §6.5): Overlay abgedunkelt, Esc schließt,
 * X oben rechts, Footer rechtsbündig (Cancel links, Primary rechts).
 * Dünner Kompatibilitäts-Wrapper über den nova-Primitives (./dialog) —
 * behält die schlanke `title`/`footer`-API der bestehenden Aufrufer.
 * Dateiname „Modal", weil „Dialog.tsx" mit dem CLI-generierten dialog.tsx
 * kollidieren würde (case-insensitive Dateisysteme).
 */
export const Dialog = DialogRoot;
export { DialogTrigger };

export function DialogContent({
  className,
  title,
  description,
  children,
  footer,
}: {
  className?: string;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <DialogContentPrimitive
      className={cn(
        'w-[calc(100vw-32px)] max-w-[560px] gap-0 bg-background p-0 shadow-modal sm:max-w-[560px]',
        className,
      )}
    >
      <DialogHeader className="gap-0.5 border-b p-4 pr-12 text-left">
        <DialogTitle className="text-md font-medium text-foreground">{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      <div className="max-h-[70vh] overflow-y-auto p-6">{children}</div>
      {footer && (
        <DialogFooter className="mx-0 mb-0 gap-2 rounded-b-xl border-t bg-background p-4">
          {footer}
        </DialogFooter>
      )}
    </DialogContentPrimitive>
  );
}

/**
 * Große Dialog-Fläche OHNE vorgegebenes Innenleben — für flächige Arbeits-/
 * Auswahl-Dialoge, die Kopf, Spalten und Fuß selbst layouten und je Spalte
 * eigenständig scrollen (Artikel-Suche, ADR 0018-Design). `DialogContent` oben
 * schreibt Padding und `max-h-[70vh]` fest und passt dafür nicht.
 *
 * `title` bleibt Pflicht — Radix braucht für den Dialog eine Überschrift, sonst
 * warnt es und Screenreader kündigen den Dialog namenlos an. Rendert der Aufrufer
 * seine Überschrift selbst, blendet `visuallyHiddenTitle` sie nur optisch aus.
 */
export function DialogSurface({
  className,
  title,
  description,
  visuallyHiddenTitle = false,
  children,
}: {
  className?: string;
  title: string;
  description?: string;
  visuallyHiddenTitle?: boolean;
  children: React.ReactNode;
}) {
  return (
    <DialogContentPrimitive
      className={cn(
        // Die Defaults des Primitives bewusst neutralisieren: `grid`/`gap-4`/`p-4`
        // und vor allem `overflow-y-auto` — sonst scrollt der ganze Dialog statt
        // der einzelnen Spalten. `sm:max-w-none`, damit der Aufrufer die Breite setzt.
        'flex flex-col gap-0 overflow-hidden overflow-y-hidden bg-background p-0 shadow-modal sm:max-w-none',
        className,
      )}
    >
      <DialogHeader className={cn('text-left', visuallyHiddenTitle && 'sr-only')}>
        <DialogTitle className="text-md font-medium text-foreground">{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      {children}
    </DialogContentPrimitive>
  );
}
